import { Component, OnInit, OnDestroy, Output, ViewChild, TemplateRef, AfterViewInit, Input } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';

import { Subscription, of, BehaviorSubject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { TargetingSearchesService } from '../../../services/targeting-searches.service';
import { JournodexEnumsService } from '../../../../Core/services/journodex-enums.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { IncludeOrExclude } from '../../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { AppearCondition, AutocompleteCustomOption } from '../../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { AutocompleteChipsOptions } from '../../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import { InputValidationModel } from '../../../../../Common/Forms/models/input-validation.model';
import { TargetingGeographyService } from '../../../../../Common/TargetingShared/services/targeting-geography-service';
import { JournodexEntityFactory } from '../../../../../Discover/Targeting/factories/journodex-entity.factory';
import { OutletModel } from '../../../../../Discover/Targeting/models/outlet.model';
import { IncludeExcludeModel } from '../../../../../Common/Misc/models/include-exclude-model';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { JournodexSubjectParentModel } from '../../../../../Common/Journodex/models/journodex.subject.model';
import { JournodexGeographyModel } from '../../../../../Common/Journodex/models/journodex-geography.model';
import { MediaMarket } from '../../../../Core/models/media-market.model';
import type { LanguageModel } from '../../../../../Core/Localization/models/language.model';
import { TargetingSearchRegionSelectDialogService } from '../../../../../Common/TargetingShared/services/targeting-search-region-select-dialog.service';
import { DataSelectDialogModel } from '../../../../../Common/Dialogs/models/data-select-dialog.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { TargetingSearchLanguageSelectDialogService } from '../../../../../Common/TargetingShared/services/targeting-search-language-select-dialog.service';
import { IncludeExcludeBrowseAllOptions } from '../../IncludeExcludeSubjectChips/include-exclude-subject-chips.component';
import { TargetingSearchFullModel } from '../../../models/targeting-search-full.model';
import { TargetingSearch } from '../../../models/targeting-search.model';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { SubjectDialogComponent } from '../SubjectDialog/subject-dialog.component';
import { MediaMarketDialogComponent } from '../MediaMarketDialog/media-market-dialog.component';
import { BrowseAllType } from '../../IncludeExcludeMediaMarketChips/include-exclude-media-market-chips.component';
import { DataSelectTakeoverPanelComponent } from '../../../../../Common/Dialogs/components/DataSelectTakeoverPanel/data-select-takeover-panel.component';


enum RegionKey {
    geographies = 'geographies',
    areasOfCoverage = 'areasOfCoverage'
}

/**
 * This component uses reactive forms.
 * Data changes in this form are passed up to the parent via the output 'valueChanges'
 * 'formReady' output allows the parent form group to append this outlet form.
 * This is useful for validation only and should not be used to communicate data.
 */
@Component({
    selector: 'agpr-outlet-search',
    templateUrl: './outlet-search.component.html',
    styleUrls: ['./outlet-search.component.scss'],
    providers: [
        TargetingSearchRegionSelectDialogService,
        TargetingSearchLanguageSelectDialogService,
    ],
    standalone: false
})
export class OutletSearchComponent implements OnInit, AfterViewInit, OnDestroy {

    outletForm: FormGroup = new FormGroup({
        outlets: new FormControl(<OutletModel[]>[]),
        outletKeywords: new FormControl(new IncludeExcludeModel<string>()),
        outletSubjects: new FormControl(new IncludeExcludeModel<JournodexSubjectParentModel>()),
        geographies: new FormControl(new IncludeExcludeModel<JournodexGeographyModel>(), [JournodexGeographyModel.geographyValidator()]),
        areasOfCoverage: new FormControl(new IncludeExcludeModel<JournodexGeographyModel>()),
        mediaMarkets: new FormControl(new IncludeExcludeModel<MediaMarket>()),
        languages: new FormControl(new IncludeExcludeModel<LanguageModel>()),
    });

    @Input() set outletData(search: TargetingSearchFullModel) {
        const filteredOutlets = search.outlets?.filter(outlet => outlet.name) || [];

        this.outletForm.setValue({
            outlets: filteredOutlets,
            outletKeywords: search.outletKeywords,
            outletSubjects: search.outletSubjects,
            geographies: search.geographies,
            areasOfCoverage: search.areasOfCoverage,
            mediaMarkets: search.mediaMarkets,
            languages: search.languages
        }, { emitEvent: false, onlySelf: true });
    }

    @Output() formReady = of(this.outletForm);

    @Output() valueChange = this.outletForm.valueChanges.pipe(
        // @hack - fixes timing issues between valueChanges, patchValue
        debounceTime(300),
        map(
            (formValue): Partial<TargetingSearchFullModel> => ({
                outlets: formValue.outlets,
                outletKeywords: formValue.outletKeywords,
                outletSubjects: formValue.outletSubjects,
                geographies: formValue.geographies,
                areasOfCoverage: formValue.areasOfCoverage,
                mediaMarkets: formValue.mediaMarkets,
                languages: formValue.languages,
            })
        )
    );

    @ViewChild('noContentMessageTemplate') noContentMessageTemplate: TemplateRef<any>;


    hasMediaMarkets: boolean = false;
    noContentMessages: { [key: string]: AutocompleteCustomOption } = {};
    previewLoading: boolean = false;
    previewCount: number = 0;
    overlayContainer: Element;

    readonly id = 'targetingOutletSearch';
    readonly regionKey = RegionKey;
    readonly messageSeverity = messageSeverityEnum;
    readonly MAX_SEARCH_SIZE = TargetingSearch.MAX_SEARCH_SIZE;
    readonly noContentMessageTypes = ['region', 'language', 'targetAudienceRegion'];
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;
    readonly keywordValidators = [
        new InputValidationModel({
            errorKey: 'Monitoring.keyWords.minimumKeywordCharacterLength',
            isValid: (input: string): boolean => {
                return input.trim().length > 1;
            }
        }),
        // validation for single keywords
        new InputValidationModel({
            errorKey: 'Monitoring.keywords.maximumKeywordCharacterLength',
            isValid: (input: string): boolean => {
                return input.trim().length < 3072;
            }
        }),
        // validation for the total length of all keywords
        new InputValidationModel({
            errorKey: 'Monitoring.keywords.maximumKeywordCharacterLength',
            isValid: (input: string): boolean => {
                const totalLength = [
                    ...(this.outletKeywords.include || []),
                    ...(this.outletKeywords.exclude || []),
                    ...(this.outletKeywords.includeAll || []),
                    input
                ]
                    .map(str => str.trim())
                    .join('')
                    .length;

                return totalLength < 3072;
            }
        })
    ];
    readonly outletNameAutocompleteOptions: AutocompleteChipsOptions = {
        customOptions: [
            {
                label: 'Discover.targeting.searchFieldLabels.names.searchFor',
                action: (searchString: string) => {
                    const outlets: OutletModel[] = this.outletForm.get('outlets').value;

                    if (searchString?.trim()?.length < 3 || outlets.find(x => x.name === searchString)) {
                        return;
                    }

                    const newOutlet = this.journodexEntityFactory.createEmptyOutlet();
                    newOutlet.name = searchString;

                    outlets.push(newOutlet);
                    this.outletForm.patchValue({'outlets' : outlets});
                },
                appearCondition: AppearCondition.always,
                clearOnSelect: true
            },
        ],
        forceSelection: false,
        suggestionsFetcher: this.targetingSearchesService.outletSearcher.bind(this.targetingSearchesService),
    };

    private subscriptions: Subscription[] = [];

    constructor(
        public targetingSearchesService: TargetingSearchesService,
        public journodexEnumsService: JournodexEnumsService,
        public targetingGeographyService: TargetingGeographyService,
        public localizationService: LocalizationService,
        protected coreDialogService: CoreDialogService,
        protected journodexEntityFactory: JournodexEntityFactory,
        protected languageSelectDialogService: TargetingSearchLanguageSelectDialogService,
        protected regionSelectDialogService: TargetingSearchRegionSelectDialogService,
    ) { }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.journodexEnumsService.getAllMediaMarkets().subscribe(mediaMarkets => {
                this.hasMediaMarkets = mediaMarkets.length > 0;
            }),
            this.targetingSearchesService.getPreviewCount().subscribe(count => {
                if (!count) {
                    this.previewCount = 0;
                    return;
                }

                this.previewCount = count;
            }),
            this.targetingSearchesService.getPreviewCountLoading().subscribe(loading => {
                this.previewLoading = loading;
            })
        );
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.prepareNoContentMessages();
        });
    }

    get outletKeywords() {
        return this.outletForm.get('outletKeywords').value;
    }

    get geographies(): FormGroup {
        return this.outletForm.get('geographies') as FormGroup;
    }

    showSubjectsBrowser(options: IncludeExcludeBrowseAllOptions) {
        const subjects = this.outletForm.get('outletSubjects').value[options.includeOrExclude];

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                selectedSubjects: subjects || [],
                initialFilterString: options?.options?.query,
                scrollToParentId: options?.options?.parent?.id
            },
            outputs: {
                dialogClose: (selectedSubjects: JournodexSubjectParentModel[]) => {
                    if (selectedSubjects === null) {
                        return;
                    }

                    const newSubject: IncludeExcludeModel<JournodexSubjectParentModel> = this.outletForm.get('outletSubjects').value;
                    newSubject[options.includeOrExclude] = selectedSubjects;

                    this.outletForm.patchValue({'outletSubjects': newSubject});
                }
            }
        };

        this.coreDialogService.open(SubjectDialogComponent, io);
    }

    openMediaMarketDialog(browseAll: BrowseAllType) {
        const io = {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                includeExclude: browseAll.includeOrExclude,
                selectedMarkets: this.outletForm.get('mediaMarkets').value[browseAll.includeOrExclude],
                scrollToChildId: browseAll.childId || null
            },
            outputs: {
                dialogClose: (selectedMediaMarkets: MediaMarket[]) => {
                    if (selectedMediaMarkets === null) {
                        return;
                    }

                    const newMediaMarket: IncludeExcludeModel<MediaMarket> = this.outletForm.get('mediaMarkets').value;
                    newMediaMarket[browseAll.includeOrExclude] = selectedMediaMarkets;

                    this.outletForm.patchValue({'mediaMarkets': newMediaMarket});
                }
            }
        };

        this.coreDialogService.open(MediaMarketDialogComponent, io);
    }

    showRegionBrowser(property: RegionKey, includeOrExclude: IncludeOrExclude) {
        const geographies = this.outletForm.get(property).value[includeOrExclude];
        this.regionSelectDialogService.prepareModelData(includeOrExclude, geographies);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.regionSelectDialog',
                dialogService: this.regionSelectDialogService,
                propagateSelection: (property === RegionKey.areasOfCoverage) ? false : true,
                dialogHeader: 'Monitoring.search.regions',
                dialogMessage: 'Monitoring.search.selectedRegionsMsg',
                leftColumnHeader: 'Monitoring.search.availableRegions',
                rightColumnHeader: 'Monitoring.search.selectedRegions',
                lockMessage: 'Discover.dialogs.browsers.lockedGeo',
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedRegions: JournodexGeographyModel[]) => {
                    if (!selectedRegions) {
                        selectedRegions = [];
                    }
                    const newGeo: IncludeExcludeModel<JournodexGeographyModel> = this.outletForm.get(property).value;
                    newGeo[includeOrExclude] = selectedRegions;

                    this.outletForm.patchValue({ [property]: newGeo});
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    showLanguageBrowser(includeOrExclude: IncludeOrExclude) {
        const languages = this.outletForm.get('languages').value[includeOrExclude];
        this.languageSelectDialogService.prepareModelData(includeOrExclude, languages);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.languageSelectDialog',
                dialogService: this.languageSelectDialogService,
                dialogHeader: 'Monitoring.result.languages',
                dialogMessage: 'Monitoring.result.selectLanguagesMsg',
                leftColumnHeader: 'Monitoring.result.availableLanguages',
                rightColumnHeader: 'Monitoring.result.selectedLanguages',
                overlayContainer: this.overlayContainer,
                filterDataType: {
                    type: 'Monitoring.result.languages',
                    noResultsLocalizationString: 'Common.forms.treeList.noResultsFeminine'
                },
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedLanguages: LanguageModel[]) => {
                    if (!selectedLanguages) {
                        selectedLanguages = [];
                    }
                    const newLang: IncludeExcludeModel<LanguageModel> = this.outletForm.get('languages').value;
                    newLang[includeOrExclude] = selectedLanguages;

                    this.outletForm.patchValue({'languages': newLang});
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    prepareNoContentMessages(): void {
        this.noContentMessageTypes.forEach(searchType => {
            const customOption: AutocompleteCustomOption = {
                appearCondition: AppearCondition.noSuggestions,
                clearOnSelect: false,
                action: () => {},
                labelTemplate: this.noContentMessageTemplate,
                labelTemplateVariable: {
                    severity: messageSeverityEnum.noContent,
                    noContentMessageLabel: `Discover.targeting.searchFieldLabels.noContentMessage.${searchType}`
                }
            };
            this.noContentMessages[searchType] = customOption;
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
